import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../Common/Loading'; 
import CustomAlert from '../Common/CustomAlert';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const styles = {
    container: { 
        position: 'absolute' as 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column' as 'column',
        fontFamily: 'Verdana, Arial, Helvetica, sans-serif', fontSize: '11px', 
        backgroundColor: '#FFFFFF', overflow: 'hidden' 
    },
    headerSection: {
        padding: '5px 10px 0px 10px',
        backgroundColor: 'transparent',
        color: 'black',
        fontWeight: 'bold' as 'bold',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        fontFamily: '"Times New Roman", Times, serif',
        fontSize: '12pt',
    },
    tableContainer: { 
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#FFFFFF'
    },
    dataTable: { 
        width: '100%', 
        borderCollapse: 'collapse' as 'collapse', 
        fontSize: '11px', 
        border: '1px solid #A0A0A0' 
    },
    th: { 
        fontFamily: 'Arial',
        backgroundColor: '#C7DDFD',
        color: '#2859ac',
        fontSize: '10pt',
        padding: '8px',
        fontWeight: 'normal' as 'normal',
        border: '1px solid #a3c0e8',
        textAlign: 'left' as 'left',
        position: 'sticky' as 'sticky',
        top: 0,
        zIndex: 1
    },
    td: { 
        padding: '8px',
        fontFamily: 'Arial',
        fontSize: '10pt',
        borderRight: '1px solid #a3c0e8',
        color: 'black',
        cursor: 'pointer'
    },
    button: {
        fontFamily: 'Arial',
        width : '90px',
        padding: '2px 10px',
        color: '#28456E',
        cursor: 'pointer',
        fontSize: '9pt',
        marginRight: '10px',
    },
    footer: {
        marginTop: 'auto',
        backgroundColor: 'none',
        padding: '8px 10px',
        borderTop: '1px solid #80A0FF',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial',
        fontSize: '10pt',
        color: '#2859ac',
        flexShrink: 0
    }
};

interface ELSelectLearningDifficultyProps {
    initialDifficulty?: string;
    onSelect?: (difficulty: string) => void;
    onClose?: () => void;
}

interface LearningDifficulty {
    difficulty: string;
}

const EL_SelectLearningDifficulty: React.FC<ELSelectLearningDifficultyProps> = (props) => {
    const [searchParams] = useSearchParams();
    const effectiveInitialDifficulty = props.initialDifficulty ?? searchParams.get('LearningDifficulty') ?? '';

    const [learningDifficulties, setLearningDifficulties] = useState<LearningDifficulty[]>([]);
    const [selectedDifficulty, setSelectedDifficulty] = useState<string>(effectiveInitialDifficulty);
    const [loading, setLoading] = useState<boolean>(true);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    useEffect(() => {
        const fetchDifficulties = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}api/support/learning-difficulties/`);
                if (!response.ok) {
                    throw new Error('Failed to fetch learning difficulties');
                }
                const data = await response.json();
                setLearningDifficulties(data);
            } catch (err) {
                setAlertInfo({ show: true, message: err instanceof Error ? err.message : 'An error occurred' });
            } finally {
                setLoading(false);
            }
        };

        fetchDifficulties();
    }, []);

    const handleRowClick = (difficulty: string) => {
        if (selectedDifficulty === difficulty) {
            setSelectedDifficulty('');
        } else {
            setSelectedDifficulty(difficulty);
        }
    };

    const handleOK = () => {
        if (props.onSelect) {
            props.onSelect(selectedDifficulty);
        } else if (window.opener && (window.opener as any).updateLearningDifficulty) {
            (window.opener as any).updateLearningDifficulty(selectedDifficulty);
            window.close();
        } else {
            console.warn("No handler available. Window.opener is missing.");
            window.close();
        }

        if (props.onClose) props.onClose();
    };

    const handleClose = () => {
        if (props.onClose) {
            props.onClose();
        } else {
            window.close();
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerSection}>
                <span>Please Select a Learning Difficulty.</span>
            </div>

            {loading && <div style={{position:'absolute', zIndex: 10}}><Loading /></div>}

            <div style={styles.tableContainer}>
                <table style={styles.dataTable}>
                    <thead>
                        <tr>
                            <th style={styles.th}>#</th>
                            <th style={styles.th}>Difficulty</th>
                        </tr>
                    </thead>
                    <tbody>
                        {learningDifficulties.length > 0 ? (
                            learningDifficulties.map((item, index) => {
                                const isSelected = selectedDifficulty === item.difficulty;
                                return (
                                    <tr 
                                        key={index} 
                                        onClick={() => handleRowClick(item.difficulty)}
                                        style={{ 
                                            backgroundColor: isSelected 
                                                ? 'orange' 
                                                : (index % 2 === 0 ? '#F4FAFF' : 'white')
                                        }}
                                    >
                                        <td style={{
                                            ...styles.td, 
                                            width: '10px',
                                            color: isSelected ? 'white' : 'black'
                                        }}>
                                            {index + 1}
                                        </td>
                                        <td style={{
                                            ...styles.td,
                                            fontWeight: 'normal' as 'normal',
                                            color: isSelected ? 'white' : 'black'
                                        }}>
                                            {item.difficulty}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td style={{...styles.td, textAlign: 'center'}}></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div style={styles.footer}>
                <button
                    style={styles.button}
                    onClick={handleOK}
                >
                    OK
                </button>
                <button
                    style={styles.button}
                    onClick={handleClose}
                >
                    Close
                </button>
            </div>

            {alertInfo.show && (
                <CustomAlert 
                    message={alertInfo.message} 
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })} 
                />
            )}
        </div>
    );
};

export default EL_SelectLearningDifficulty;